import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../../assets/styles/report/SiteAnalysis.module.scss';
import ReportLayout from '../../components/Layout/ReportLayout';
import { useReportFilter } from '../../contexts/ReportContext';
import { useProjectContext } from "../../contexts/ProjectContext";

// ============================================================================
// Types
// ============================================================================

interface Ga4PageData {
  path: string;
  sessions: number;
  users: number;
  avgTime: string;
  bounceRate: string;
  engagementRate: string;
  scrollRatePC: string;
  scrollRateSP: string;
}

interface UserAttributes {
  area: Array<{ label: string; val: number; color: string }>;
  gender: { male: number; female: number; unknown: number };
  device: { mobile: number; desktop: number; tablet: number };
}

interface AiRecommend {
  type: 'improvement' | 'contribution';
  title: string;
  page: string;
  reason: string;
  action: string;
}

interface SiteAnalysisResponse {
  ga4Data: Ga4PageData[];
  userAttributes: UserAttributes;
  aiRecommendations: AiRecommend[];
}

// ============================================================================
// Main Component
// ============================================================================
const SiteAnalysis: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const { selectedPeriod, customStart, customEnd } = useReportFilter();

  // Data States
  const [data, setData] = useState<SiteAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ==========================================================================
  // Fetch Logic
  // ==========================================================================
  const fetchSiteAnalysis = useCallback(async () => {
    if (!selectedProjectId) return;
    setIsLoading(true);

    try {
      const params: Record<string, string> = {
        project_id: String(selectedProjectId),
        period: selectedPeriod,
      };

      if (selectedPeriod === 'custom' && customStart && customEnd) {
        params.start = customStart;
        params.end = customEnd;
      }

      const queryParams = new URLSearchParams(params);
      const url = `${process.env.REACT_APP_BACKEND_URL}/get-site-analysis?${queryParams}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const resData = await response.json();
        setData(resData);
      } else {
        console.error("Failed to fetch site analysis data");
      }
    } catch (error) {
      console.error("SiteAnalysis Fetch Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProjectId, selectedPeriod, customStart, customEnd]);

  useEffect(() => {
    fetchSiteAnalysis();
  }, [fetchSiteAnalysis]);

  // ==========================================================================
  // Render
  // ==========================================================================
  if (isLoading || !data) {
    return (
      <ReportLayout>
        <div className={styles.loading}>データを読み込み中...</div>
      </ReportLayout>
    );
  }

  const {
    ga4Data = [],
    aiRecommendations = [],
    userAttributes = {
      area: [],
      gender: { male: 0, female: 0, unknown: 0 },
      device: { mobile: 0, desktop: 0, tablet: 0 }
    }
  } = data;

  const getGenderDisplayData = () => {
    const { male = 0, female = 0, unknown = 0 } = userAttributes.gender;

    // 全て 0 もしくは null の場合
    if (!male && !female && !unknown) {
      return { male: 0, female: 0, unknown: 100 };
    }
    return { male, female, unknown };
  };
  const genderDisplay = getGenderDisplayData();

  return (
    <ReportLayout>
      <div className={styles.reportContent}>
        <div className={styles.scrollArea}>

          <div className={styles.bottomFlex}>
            {/* 1. GA4 コンテンツ分析テーブル */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles.sectionGa4Table}
            >
              <div className={styles.tableContainer}>
                <table className={styles.analysisTable}>
                  <thead>
                    <tr>
                      <th>ページパス</th>
                      <th>セッション</th>
                      <th>ユーザー数</th>
                      <th>平均滞在時間</th>
                      <th>直帰率</th>
                      <th>E率</th>
                      <th>スクロール(PC)</th>
                      <th>スクロール(SP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Data.length > 0 ? (
                      ga4Data.map((row, idx) => (
                        <motion.tr
                          key={idx}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <td className={styles.tdPath}>{row.path}</td>
                          <td>{row.sessions.toLocaleString()}</td>
                          <td>{row.users.toLocaleString()}</td>
                          <td>{row.avgTime}</td>
                          <td>{row.bounceRate}</td>
                          <td>{row.engagementRate}</td>
                          <td>{row.scrollRatePC}</td>
                          <td>{row.scrollRateSP}</td>
                        </motion.tr>
                      ))
                    ) : (
                      <motion.tr
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}
                      >
                        <td colSpan={8}>対象期間のデータがありません</td>
                      </motion.tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.section>

            {/* 2. ユーザー属性カード */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className={styles.sectionAttributes}
            >
              <div className={styles.attributeCard}>
                <h2 className={styles.subTitle}>ユーザー属性</h2>

                {/* エリア分布 */}
                <div className={`${styles.attrSection} ${styles.mb24}`}>
                  <label className={styles.areaLabelBlock}>市区町村別分布(上位10件)</label>
                  <div className={styles.areaList}>
                    {userAttributes.area.length > 0 ? (
                      userAttributes.area.map((area, i) => (
                        <div key={i} className={styles.areaRow}>
                          <span className={styles.areaLabel}>{area.label}</span>
                          <div className={styles.areaBarBg}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${area.val}%` }}
                              transition={{ duration: 1, delay: 0.5 + (i * 0.1) }}
                              className={styles.areaBarFill}
                              style={{ background: area.color }}
                            />
                          </div>
                          <span className={styles.areaValue}>{area.val}%</span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.noDataSmall}>データがありません</div>
                    )}
                  </div>
                </div>

                {/* 性別比率 */}
                <div className={styles.attrSection}>
                  <label>性別比率</label>
                  <div className={styles.genderBar}>
                    {/* Male: 0より大きい場合のみ表示 */}
                    {genderDisplay.male > 0 && (
                      <div className={`${styles.barSell} ${styles.barMale}`} style={{ width: `${genderDisplay.male}%` }}>
                        <span>M {genderDisplay.male}%</span>
                      </div>
                    )}

                    {/* Female: 0より大きい場合のみ表示 */}
                    {genderDisplay.female > 0 && (
                      <div className={`${styles.barSell} ${styles.barFemale}`} style={{ width: `${genderDisplay.female}%` }}>
                        <span>F {genderDisplay.female}%</span>
                      </div>
                    )}

                    {/* Unknown: 0より大きい場合のみ表示 */}
                    {genderDisplay.unknown > 0 && (
                      <div className={`${styles.barSell} ${styles.barOther}`} style={{ width: `${genderDisplay.unknown}%` }}>
                        <span>? {genderDisplay.unknown}%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* デバイス比率 */}
                <div className={styles.attrSection}>
                  <label>デバイス比率</label>
                  <div className={styles.deviceList}>
                    <div className={styles.deviceItem}>
                      <span>Mobile</span><b>{userAttributes.device.mobile}%</b>
                    </div>
                    <div className={styles.deviceItem}>
                      <span>Desktop</span><b>{userAttributes.device.desktop}%</b>
                    </div>
                    <div className={styles.deviceItem}>
                      <span>Tablet</span><b>{userAttributes.device.tablet}%</b>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>

          {/* 経路分析 (Path Analysis) - Updated Flow (TOP -> Split -> Form) */}
          <section className={styles.sectionPathAnalysis}>
            <h2 className={styles.pathTitle}>経路分析（ユーザーフロー）</h2>
            <div className={styles.pathFlowContainer}>
              <div className={styles.pathGrid}>

                {/* Step 0: TOP */}
                <div style={{ textAlign: 'center' }}>
                  <div className={`${styles.stepBox} ${styles.stepTop}`}>
                    TOPページ
                    <div className={styles.stepSubText}>45,000 UU</div>
                  </div>
                </div>

                {/* Arrow (Top -> Middle) */}
                <div className={styles.arrowContainer}>
                  <div className={styles.arrowLabel}>60%</div>
                  <div className={styles.arrowLine}></div>
                </div>

                {/* Step 1: Intermediate (Split) */}
                <div className={styles.splitGroup}>
                  <div className={`${styles.stepBox} ${styles.stepSplit}`}>
                    製品詳細
                    <div className={`${styles.stepSubText} ${styles.small}`}>15,000 UU</div>
                  </div>
                  <div className={`${styles.stepBox} ${styles.stepSplit}`}>
                    料金ページ
                    <div className={`${styles.stepSubText} ${styles.small}`}>8,500 UU</div>
                  </div>
                  <div className={`${styles.stepBox} ${styles.stepSplit}`}>
                    ブログ
                    <div className={`${styles.stepSubText} ${styles.small}`}>3,500 UU</div>
                  </div>
                </div>

                {/* Detailed Arrows (Middle -> Form) */}
                <div className={styles.complexArrowArea}>
                  <div className={styles.complexArrowContainer}>
                    {/* Product -> Form */}
                    <div className={`${styles.complexArrow} ${styles.top}`}></div>
                    <span className={`${styles.arrowPercent} ${styles.top}`}>15%</span>

                    {/* Pricing -> Form */}
                    <div className={`${styles.complexArrow} ${styles.middle}`}></div>
                    <span className={`${styles.arrowPercent} ${styles.middle}`}>30%</span>

                    {/* Blog -> Form */}
                    <div className={`${styles.complexArrow} ${styles.bottom}`}></div>
                    <span className={`${styles.arrowPercent} ${styles.bottom}`}>5%</span>
                  </div>
                </div>

                {/* Step 2: Form Input */}
                <div style={{ textAlign: 'center' }}>
                  <div className={`${styles.stepBox} ${styles.stepForm}`}>
                    フォーム入力
                    <div className={styles.stepSubText}>4,050 UU</div>
                  </div>
                </div>

                {/* Arrow */}
                <div className={styles.arrowContainer}>
                  <div className={styles.arrowLabel}>60%</div>
                  <div className={styles.arrowLine}></div>
                </div>

                {/* Step 3: Form Confirm */}
                <div style={{ textAlign: 'center' }}>
                  <div className={`${styles.stepBox} ${styles.stepConfirm}`}>
                    フォーム確認
                    <div className={styles.stepSubText}>2,430 UU</div>
                  </div>
                </div>

                {/* Arrow */}
                <div className={styles.arrowContainer}>
                  <div className={styles.arrowLabel}>50%</div>
                  <div className={styles.arrowLine}></div>
                </div>

                {/* Step 4: Thanks */}
                <div style={{ textAlign: 'center' }}>
                  <div className={`${styles.stepBox} ${styles.stepThanks}`}>
                    サンクスページ
                    <div className={styles.stepSubText}>1,215 CV</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 4. AI Inside Insights */}
          <section className={styles.sectionAiRecommend}>
            <h2 className={styles.subTitle}>AI Inside Insights</h2>
            <div className={styles.recommendGrid}>
              <AnimatePresence>
                {aiRecommendations.map((rec, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + (idx * 0.1) }}
                    className={`${styles.recommendCard} ${rec.type === 'improvement' ? styles.typeImprove : styles.typeContribute}`}
                  >
                    <div className={styles.recHeader}>
                      <span className={styles.recBadge}>{rec.title}</span>
                      <span className={styles.recPage}>{rec.page}</span>
                    </div>
                    <div className={styles.recContent}>
                      <p className={styles.recReason}>{rec.reason}</p>
                      <div className={styles.recActionBox}>
                        <span className={styles.actionLabel}>AI Suggestion</span>
                        <p className={styles.actionText}>{rec.action}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </section>
        </div>
      </div>
    </ReportLayout>
  );
};

export default SiteAnalysis;
